import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Switch,
  Tag,
} from "antd";
import { CopyOutlined, LeftOutlined } from "@ant-design/icons";
import BasicButtonPrimary from "components/BasicButtonPrimary";
import AIAssistantService from "services/aiAssistantService";
import { useTranslation } from "react-i18next";
import CopyToClipboard from "react-copy-to-clipboard";

export default function AIAssistantSetting() {
  const [listAIAssistant, setListAIAssistant] = useState([]);
  const [AIAssistantCode, setAIAssistantCode] = useState(null);

  const fetchAIAssistant = async () => {
    try {
      const res = await AIAssistantService.Update();
      if (res?.isSuccess) {
        setListAIAssistant(res.data);
      } else {
        setListAIAssistant([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setListAIAssistant([]);
    }
  };

  useEffect(() => {
    fetchAIAssistant();
  }, []);

  return (
    <div>
      {!AIAssistantCode && (
        <div className="d-flex flex-wrap gap-2">
          {listAIAssistant.map((item, index) => (
            <div
              key={index}
              className="d-flex align-items-center p-2 border gap-2 pointer"
              style={{ height: 80, borderRadius: 8, minWidth: 200 }}
              onClick={() => setAIAssistantCode(item?.partyCode)}
            >
              <img
                src={process.env.PUBLIC_URL + "/assets/images/icon-chatbot.png"}
                alt=""
                width={50}
              />
              <span>{item.partyCode}</span>
            </div>
          ))}
        </div>
      )}

      {AIAssistantCode && (
        <PaymentMethodByType
          AIAssistantCode={AIAssistantCode}
          setAIAssistantCode={setAIAssistantCode}
          listAIAssistant={listAIAssistant}
          fetchAIAssistant={fetchAIAssistant}
        />
      )}
    </div>
  );
}

function PaymentMethodByType({
  AIAssistantCode,
  setAIAssistantCode,
  listAIAssistant,
  fetchAIAssistant,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formUpdate] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [activeStatus, setActiveStatus] = useState(false);

  const currentAssistant = listAIAssistant.find(
    (item) => item.partyCode === AIAssistantCode
  );

  useEffect(() => {
    if (currentAssistant?.partyEnableStatus !== undefined) {
      setActiveStatus(currentAssistant.partyEnableStatus);
    }
  }, [currentAssistant]);

  const requiredData = currentAssistant?.partyRequiredData
    ? JSON.parse(currentAssistant.partyRequiredData)
    : {};

  const merchantCode = requiredData.merchantCode || "";

  const TestConfig = async (payload) => {
    try {
      const res = await AIAssistantService.TestConfig(payload);
      return res;
    } catch (error) {
      console.error("TestConfig error:", error);
      return null;
    }
  };

  const handleSubmit = async (payload) => {
    setLoading(true);
    try {
      const res = await TestConfig(payload);
      fetchAIAssistant();
      if (res?.isSuccess) {
        notification.success({ message: t("setting.ai.connectingSuccess") });
      } else {
        notification.error({ message: t("setting.ai.connectingFailed") });
      }
    } catch (error) {
      notification.error({ message: t("setting.ai.connectingFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = async (checked) => {
    setActiveStatus(checked);
    try {
      const data = {
        data: {
          partyEnableStatus: checked ? 1 : 0,
        },
      };
      const res = await AIAssistantService.Update(data);

      if (res?.isSuccess) {
        notification.success({
          message: checked
            ? t("setting.ai.activate")
            : t("setting.ai.deactivate"),
        });
      } else {
        notification.error({ message: t("setting.ai.updateStatusFailed") });
        setActiveStatus(!checked); // rollback
      }
    } catch (err) {
      console.error("Update status error:", err);
      notification.error({ message: t("setting.ai.updateStatusFailed") });
      setActiveStatus(!checked); // rollback
    }
  };

  const dataScript = useMemo(() => {
    const result = {
      inboxChannelScriptText: "",
      chatboxUrl: "",
      inboxChannelScriptUrl: "",
    };

    if (
      currentAssistant?.partyActiveStatus &&
      currentAssistant?.integrationMetadata
    ) {
      const integrationMetadata = JSON.parse(
        currentAssistant?.integrationMetadata || "{}"
      );
      const inboxChannelScriptText =
        integrationMetadata?.merchantInboxes?.[0]?.inboxChannelScriptText;
      const inboxChannelScriptUrl =
        integrationMetadata?.merchantInboxes?.[0]?.inboxChannelScriptUrl;
      const chatboxUrl = integrationMetadata?.chatboxUrl;

      result.inboxChannelScriptText = inboxChannelScriptText;
      result.inboxChannelScriptUrl = inboxChannelScriptUrl;
      result.chatboxUrl = chatboxUrl;
    }
    return result;
  }, [currentAssistant]);

  const titleCopyInput = ({ title, url }) => {
    return (
      <div className="d-flex align-items-center gap-2">
        <span>{title}</span>
        <CopyToClipboard
          onCopy={() =>
            notification.success({
              message: "Sao chép thành công",
            })
          }
          text={url || ""}
        >
          <CopyOutlined style={{ fontSize: 18 }} />
        </CopyToClipboard>
      </div>
    );
  };

  useEffect(() => {
    form.setFieldsValue({
      merchantCode: merchantCode,
      inboxChannelScriptText: dataScript?.inboxChannelScriptText,
      inboxChannelScriptUrl: dataScript?.inboxChannelScriptUrl,
      chatboxUrl: dataScript?.chatboxUrl,
    });
  }, [merchantCode, dataScript]);

  useEffect(() => {
    const partyRequiredData = currentAssistant?.partyRequiredData
      ? JSON.parse(currentAssistant.partyRequiredData)
      : {};
    formUpdate.setFieldsValue({
      openaiApikey: partyRequiredData?.openaiApikey,
      aiAgentType: partyRequiredData?.aiAgentType,
    });
  }, [currentAssistant]);

  const AI_AGENT_TYPE = [
    {
      value: 1,
      label: (
        <div className="d-flex align-items-center">
          <img
            src="/assets/images/chatGPT.png"
            width={18}
            height={18}
            alt=""
          ></img>
          <span className="ms-2">ChatGPT</span>
        </div>
      ),
      icon: "/public/assets/images/chatGPT.png",
      disabled: false,
    },
    {
      value: 2,
      label: (
        <div className="d-flex align-items-center">
          <img
            src="/assets/images/deepSeek.png"
            width={20}
            height={20}
            alt=""
          ></img>
          <span className="ms-2">DeepSeek</span>
        </div>
      ),
      disabled: true,
    },
    {
      value: 3,
      label: (
        <div className="d-flex align-items-center">
          <img
            src="/assets/images/gemini.png"
            width={20}
            height={20}
            alt=""
          ></img>
          <span className="ms-2">Gemini</span>
        </div>
      ),
      disabled: true,
    },
    {
      value: 4,
      label: (
        <div className="d-flex align-items-center">
          <img
            src="/assets/images/grok.png"
            width={20}
            height={20}
            alt=""
          ></img>
          <span className="ms-2">Grok</span>
        </div>
      ),
      disabled: true,
    },
  ];

  const handleUpdate = async (values) => {
    const payload = {
      data: {
        partyRequiredData: {
          openaiApikey: values.openaiApikey,
          aiAgentType: values.aiAgentType,
        },
      },
    };
    setLoadingSubmit(true);
    const res = await AIAssistantService.Update(payload);
    setLoadingSubmit(false);
    if (res?.isSuccess) {
      notification["success"]({
        message: t("updateSuccess"),
      });
      fetchAIAssistant();
    } else {
      notification["error"]({
        message: t("updateFailed"),
      });
    }
  };

  return (
    <div>
      <BasicButtonPrimary
        type="default"
        icon={<LeftOutlined />}
        onClick={() => setAIAssistantCode(null)}
      >
        Quay lại
      </BasicButtonPrimary>

      <Card className="my-3">
        <div className="section-title mb-2">
          {`${t("setting.ai.settings")} ${currentAssistant?.partyCode}`}
        </div>
        {/* <div className="">{t("smartgit.installGuide")}</div> */}
        {/* <h5>{`${t("setting.ai.settings")} ${currentAssistant?.partyCode}`}</h5> */}

        {/* Toggle trạng thái hoạt động */}
        <div className="mb-3">
          <div style={{ marginBottom: 8 }}>
            {t("setting.ai.activeStatus")}:{" "}
          </div>
          <Switch checked={activeStatus} onChange={handleToggleChange} />
        </div>
        <Row>
          <Col xs={24} sm={24} md={19} lg={12}>
            <Form layout="vertical" form={form}>
              <Form.Item label={"Mã (Merchant Code)"} name="merchantCode">
                <Input readOnly />
              </Form.Item>
              <Form.Item
                label={titleCopyInput({
                  title: "Liên kết trò chuyện (Chatbox URL)",
                  url: dataScript?.chatboxUrl,
                })}
                name="chatboxUrl"
                hidden={!dataScript?.chatboxUrl}
              >
                <Input.TextArea readOnly autoSize={{ minRows: 1 }} />
              </Form.Item>
              <Form.Item
                label={titleCopyInput({
                  title: "Liên kết nhúng (Inbox Channel Script URL)",
                  url: dataScript?.inboxChannelScriptUrl,
                })}
                name="inboxChannelScriptUrl"
                hidden={!dataScript?.inboxChannelScriptUrl}
              >
                <Input.TextArea readOnly autoSize={{ minRows: 1 }} />
              </Form.Item>
              <Form.Item
                label={titleCopyInput({
                  title: "Mã nhúng (Inbox Channel Script Text)",
                  url: dataScript?.inboxChannelScriptText,
                })}
                name="inboxChannelScriptText"
                hidden={!dataScript?.inboxChannelScriptText}
              >
                <Input.TextArea readOnly autoSize={{ minRows: 1 }} />
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col xs={24} sm={24} md={19} lg={12}>
            <Form layout="vertical" form={formUpdate} onFinish={handleUpdate}>
              <Form.Item
                label={"Apikey (OpenAI API Key)"}
                hidden={Object.keys(requiredData).length === 0}
                name={"openaiApikey"}
                // rules={[
                //   {
                //     required: true,
                //     message: "Vui lòng nhập Apikey",
                //   },
                // ]}
              >
                <Input placeholder="Apikey" />
              </Form.Item>
              <Form.Item
                label={"Loại AI (AI Agent Type)"}
                name={"aiAgentType"}
                hidden={Object.keys(requiredData).length === 0}
                // rules={[
                //   {
                //     required: true,
                //     message: "Vui lòng chọn AI Agent",
                //   },
                // ]}
              >
                <Select options={AI_AGENT_TYPE} placeholder="AI Agent" />
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <div className="mb-3">
          <div style={{ marginBottom: 8 }}>
            {t("setting.ai.connectingStatus")}:{" "}
          </div>
          <Tag color={currentAssistant?.partyActiveStatus ? "green" : ""}>
            {currentAssistant?.partyActiveStatus
              ? t("setting.ai.connected")
              : t("setting.ai.disconnected")}
          </Tag>
        </div>
        {/* <BasicButtonPrimary
         htmlType="submit"
         text={t("setting.ai.testing")}
         loading={loading}
       /> */}
        <div className="d-flex align-items-center gap-2">
          {Object.keys(requiredData).length > 0 && (
            <Button
              type="primary"
              loading={loadingSubmit}
              onClick={() => formUpdate.submit()}
              htmlType="submit"
            >
              {t("update")}
            </Button>
          )}

          <Button
            loading={loading}
            onClick={() =>
              handleSubmit({
                openaiApikey:
                  formUpdate.getFieldValue("openaiApikey") || null,
                aiAgentType:
                  formUpdate.getFieldValue("aiAgentType") || null,
              })
            }
            type="submit"
            htmlType="button"
          >
            {t("setting.ai.testing")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
